import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Target, Users, Shield, Award } from 'lucide-react';
import aboutBg from '../assets/images/about-bg.png';

const AboutSection = styled.section`
  padding-top: 12rem;
  background-color: var(--bg-primary);
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url(${aboutBg});
    background-size: cover;
    background-position: center;
    opacity: 0.15;
    z-index: 0;
    mix-blend-mode: screen;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 150px;
    background: linear-gradient(to top, var(--bg-secondary), transparent);
    z-index: 1;
  }
`;

const BackgroundGlow = styled.div`
  position: absolute;
  top: -20%;
  left: 50%;
  transform: translateX(-50%);
  width: 800px;
  height: 800px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
  z-index: 0;
  pointer-events: none;
`;

const ValuesSection = styled.section`
  background-color: var(--bg-secondary);
  text-align: center;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-top: 4rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled(motion.div)`
  padding: 3rem 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  .icon-wrapper {
    width: 70px;
    height: 70px;
    border-radius: 20px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
    color: var(--accent-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2rem;
    box-shadow: 0 10px 30px -10px rgba(59, 130, 246, 0.3);
  }

  h3 {
    color: var(--text-primary);
    font-size: 1.5rem;
    margin-bottom: 1rem;
    font-weight: 600;
  }

  p {
    color: var(--text-secondary);
    line-height: 1.6;
  }
`;

const StoryCard = styled(motion.div)`
  text-align: left;
  max-width: 800px;
  margin: 4rem auto;
  
  h3 {
    color: var(--text-primary);
    font-size: 2rem;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    
    &::before {
      content: '';
      display: block;
      width: 40px;
      height: 3px;
      background: var(--accent-primary);
    }
  }

  p {
    color: var(--text-secondary);
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
  }
`;

const About = () => {

  const values = [
    {
      icon: <Target size={32} />,
      title: 'Innovation Focus',
      description: 'We aggressively push the boundaries of what is possible with applied AI and cutting-edge NLP technology.'
    },
    {
      icon: <Users size={32} />,
      title: 'Strategic Partnership',
      description: 'We integrate deeply with our clients, architecting solutions that align perfectly with business trajectories.'
    },
    {
      icon: <Shield size={32} />,
      title: 'Absolute Security',
      description: 'Enterprise-grade data privacy and military-grade security are engineered into the core of every deployment.'
    },
    {
      icon: <Award size={32} />,
      title: 'Relentless Excellence',
      description: 'We do not settle. We strive for absolute excellence in model performance and operational delivery.'
    }
  ];

  return (
    <>
      <AboutSection>
        <BackgroundGlow />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-subtitle">About Us</h2>
            <h1 className="section-title">Architecting the <span className="gradient-text-accent">AI Frontier</span></h1>
            <p className="section-description" style={{ margin: '0 auto', fontSize: '1.25rem' }}>
              We are an elite collective of AI researchers and engineers dedicated to
              building next-generation intelligence for the modern enterprise.
            </p>
          </motion.div>

          <StoryCard
            className="card glass"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h3 className="gradient-text">Our Genesis</h3>
            <p>
              ContextJet AI was forged from a singular, uncompromising vision: to bridge the chasm
              between theoretical AI research and high-impact enterprise applications. Our founders,
              veterans of elite technology organizations, recognized that true business value requires
              more than just models - it requires meticulously engineered intelligence pipelines.
            </p>
            <p>
              Today, we operate globally, deploying specialized NLP systems that analyze immense
              volumes of unstructured data, uncovering insights and driving automation at a scale
              that was unimaginable just a few years ago. We don't just build software; we build the
              cognitive nervous systems for tomorrow's industry leaders.
            </p>
            <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--border-subtle)' }}>
              <p style={{ fontSize: '1rem', color: 'var(--text-tertiary)', marginBottom: '0.5rem' }}>
                Curious about the minds behind the intelligence?
              </p>
              <a
                href="https://www.contextjetai.com"
                target="_blank"
                rel="noopener noreferrer"
                className="glow-btn"
                style={{ display: 'inline-flex', padding: '0.75rem 1.5rem', fontSize: '1rem' }}
              >
                Meet the Core Team
              </a>
            </div>
          </StoryCard>
        </div>
      </AboutSection>


      <ValuesSection>
        <div className="container">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="section-subtitle">Our Core Directives</h2>
            <h3 className="section-title">The Principles We <span className="gradient-text">Execute</span> By</h3>

            <ValuesGrid>
              {values.map((value, index) => (
                <ValueCard
                  key={index}
                  className="card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="icon-wrapper">{value.icon}</div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </ValueCard>
              ))}
            </ValuesGrid>
          </motion.div>
        </div>
      </ValuesSection>
    </>
  );
};

export default About;